import React, {
    forwardRef,
    HTMLAttributes,
    memo,
    ReactElement,
} from "react";
// 1. Import the new CSS file
import "./formField.module.css";

// The props interface remains the same
export interface FormFieldUIProps {
    uiInline?: boolean;
    uiNoMargin?: boolean;
    uiLabel?: string | ReactElement<any>;
    uiLabelAlignment?: "left" | "center";
    uiNoControlWrap?: boolean;
    uiLabelFor?: string;
    uiRequired?: boolean;
    uiAfterControl?: React.ReactNode;
    uiErrorAlignment?: "left" | "center" | "right";
    uiLabelFlex?: string;
}

export type FormFieldProps = HTMLAttributes<HTMLDivElement> & FormFieldUIProps;

/**
 * Generic form field. Supports both inline / column controls and label
 */
const FormField = memo(
    forwardRef<HTMLDivElement, FormFieldProps>((props, ref) => {
        const {
            // UI Props
            uiInline,
            uiNoMargin,
            uiLabel,
            uiLabelAlignment,
            uiNoControlWrap,
            uiLabelFor,
            uiRequired,
            uiAfterControl,
            uiErrorAlignment,
            uiLabelFlex,

            // Standard HTML Props
            children,
            className,
            style,
            ...other
        } = props;

        // 2. Build the list of CSS classes
        const classNames = [
            "form-field", // Base class
            uiInline ? "form-field-inline" : "",
            uiNoMargin ? "form-field-no-margin" : "",
            uiRequired ? "form-field-required" : "",
            className, // Include any classes passed from the parent
        ]
            .filter(Boolean)
            .join(" ");

        // 3. Build the inline style object for dynamic properties
        // We pass them to CSS as custom properties (variables)
        const inlineStyles: React.CSSProperties = {
            ...style, // Preserve any styles passed from the parent
        };

        // 4. Render the simple JSX structure
        return (
            <div
                {...other}
                className={classNames}
                ref={ref}
                style={inlineStyles}
            >
                {uiLabel && (
                    <label className="label" htmlFor={uiLabelFor}>
                        {uiLabel}
                    </label>
                )}
                
                {/* Conditional rendering of the .control wrapper */}
                {uiNoControlWrap && children}
                {!uiNoControlWrap && <div className="control">{children}</div>}

                {uiAfterControl && uiAfterControl}
            </div>
        );
    })
);

FormField.displayName = "FormField";

export default FormField;